import { useCallback } from 'react'

import _ from 'lodash'
import { shallowEqual } from 'react-redux'

import { update, save } from 'store/assessment/assessmentSlice'
import { Assessment } from 'store/assessment/types'
import { questionSessionActions } from 'store/questionSession/questionSessionSlice'
import { QuestionOption } from 'store/questionTypes'

import { State } from 'store/state'

import { scrollTop } from 'utils/scrollTop'

import { useAppDispatch, useAppSelector } from '../store/hooks'

import { buildOptions } from './buildOptions'
import { shouldSaveAssessment } from './shouldSaveAssessment'

export const useSaveAnswer = () => {
  const dispatch = useAppDispatch()
  const { currentAssessment, question } = useAppSelector(
    (state: State) => ({
      currentAssessment: state.questionSessionReducer.assessment,
      question: state.questionSessionReducer.question,
    }),
    shallowEqual
  )
  const saveAssessment = useCallback(
    (assessmentToSave: Partial<Assessment>, id?: string) => {
      if (id && !_.isEqual(currentAssessment, assessmentToSave)) {
        dispatch(update({ id, assessment: assessmentToSave }))
      } else {
        dispatch(save(assessmentToSave))
      }
    },
    [currentAssessment, dispatch]
  )
  const onSubmitAssessment = ({
    optionOrOptionsSelected,
    getAssessmentToSave,
    skip,
  }: {
    optionOrOptionsSelected: number | number[] | undefined
    getAssessmentToSave: (
      options: QuestionOption[],
      totalScore: number
    ) => Partial<Assessment>
    skip: boolean
  }) => {
    const options = buildOptions(
      (question?.options || []) as QuestionOption[],
      optionOrOptionsSelected
    )
    const totalScore = options
      .map(({ score }) => +score)
      .reduce((total, score) => total + score, 0)

    const assessmentToSave = getAssessmentToSave(options, totalScore)

    if (
      shouldSaveAssessment({
        assessment: currentAssessment,
        assessmentToSave,
      }) ||
      (skip && !currentAssessment?.skipped)
    ) {
      saveAssessment(assessmentToSave, currentAssessment?.id)
    } else {
      dispatch(questionSessionActions.next())
      scrollTop()
    }
  }

  return { onSubmitAssessment }
}
